// ecommerce-frontend/src/pages/HomePage.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, User, Search, ShoppingBag, TrendingUp,
  Smartphone, Laptop, Tv, Home, Sofa, 
  Shirt, Footprints, Watch, Sparkles, Heart,
  Dumbbell, Baby, Book, Apple, Car, Hammer,
  Gem, Briefcase, Package, ChevronDown, Menu, X,
  Clock, MapPin, Facebook, MessageCircle, Camera, 
  Monitor, Phone, Mail, MapPinned, Settings, LogOut,
  ChevronLeft, ChevronRight, Star, Plus , HelpCircle ,  ShieldCheck ,Info
} from 'lucide-react';
import { productsAPI, cartAPI, authAPI } from '../services/api';

// Mapping des catégories avec leurs icônes
const categoryIcons = {
  'Électroménager': Home,
  'Smartphones': Smartphone,
  'Ordinateurs & Tablettes': Laptop,
  'Télévisions & Audio': Tv,
  'Vêtements Homme': Shirt,
  'Vêtements Femme': Shirt,
  'Chaussures': Footprints,
  'Accessoires de Mode': Watch,
  'Beauté & Parfums': Sparkles,
  'Santé & Bien-être': Heart,
  'Sports & Loisirs': Dumbbell,
  'Jouets & Enfants': Baby,
  'Maison & Décoration': Home,
  'Meubles': Sofa,
  'Livres & Médias': Book,
  'Alimentation & Boissons': Apple,
  'Automobile & Moto': Car,
  'Jardin & Bricolage': Hammer,
  'Bijoux & Montres': Gem,
  'Bagagerie & Voyage': Briefcase
};

export default function HomePage() {
  const navigate = useNavigate();
  const categoryScrollRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // NOUVEL ÉTAT
  const [showCategories, setShowCategories] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true); // NOUVEL ÉTAT
  const [user, setUser] = useState(authAPI.getCurrentUser());
  

  useEffect(() => {
    if (user && showWelcomeMessage) {
      const timer = setTimeout(() => {
        setShowWelcomeMessage(false);
      }, 10000); // 10 secondes
      
      return () => clearTimeout(timer);
    }
  }, [user, showWelcomeMessage]);

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
  try {
    const [productsData, categoriesData] = await Promise.all([
      productsAPI.getProducts(),
      productsAPI.getCategories()
    ]);

    const productsList = productsData.results || productsData || [];
    setProducts(productsList);
    setAllProducts(productsList);
    setCategories(categoriesData.results || categoriesData || []);

    if (authAPI.isAuthenticated()) {
      const cartData = await cartAPI.getCart();
      setCartCount(cartData.items_count || 0);
    }
  } catch (error) {
    console.error('Erreur chargement données:', error);
    // 🚩 DÉFINIR L'ERREUR ICI
    setError("Impossible de charger les données. Veuillez vérifier votre connexion au serveur."); 
  } finally {
    setLoading(false);
  }
};

  const filterByCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      const filtered = allProducts.filter(product => product.category === categoryId);
      setProducts(filtered);
    } else {
      setProducts(allProducts);
    }
    setSearchQuery('');
  };

  const toggleFavorite = (product, e) => {
    e.stopPropagation();
    try {
      const savedFavorites = localStorage.getItem('favorites');
      let favList = savedFavorites ? JSON.parse(savedFavorites) : [];
      
      const isFavorite = favorites.has(product.id);
      
      if (isFavorite) {
        favList = favList.filter(fav => fav.id !== product.id);
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(product.id);
          return newFavorites;
        });
      } else {
        favList.push(product);
        setFavorites(prev => new Set(prev).add(product.id));
      }
      
      localStorage.setItem('favorites', JSON.stringify(favList));
    } catch (error) {
      console.error('Erreur toggle favoris:', error);
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      alert(`Merci ! Vous êtes inscrit à notre newsletter avec ${newsletterEmail}`);
      setNewsletterEmail('');
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSelectedCategory(null);
    if (searchQuery.trim()) {
      const filtered = allProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setProducts(filtered);
    } else {
      setProducts(allProducts);
    }
  };

  useEffect(() => {
    if (searchQuery === '' && !selectedCategory) {
      setProducts(allProducts);
    }
  }, [searchQuery, allProducts, selectedCategory]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/400x400?text=Pas+d%27image';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  const getUserAvatar = () => {
    if (user?.profile_photo) return getImageUrl(user.profile_photo);
    if (user?.profile_photo_url) return user.profile_photo_url;
    return null;
  };

  const getProductImages = (product) => {
    const images = [product.image];
    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        if (img.image) images.push(img.image);
      });
    }
    return images.filter(Boolean);
  };

  const nextImage = (productId, imagesLength, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % imagesLength
    }));
  };

  const prevImage = (productId, imagesLength, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  const getTimeAgo = (createdAt) => {
    if (!createdAt) return '1 jour';
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now - created) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    else if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    else return `${Math.floor(diffInMinutes / 1440)}j`;
  };

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = categoryScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      categoryScrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const checkScroll = () => {
    if (categoryScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = categoryScrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const scrollElement = categoryScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        scrollElement.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [categories]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl text-white">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Formes décoratives */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-400/10 rounded-full blur-3xl"></div>

      {/* Header */}
      <header className="bg-white/10 backdrop-blur-2xl shadow-2xl sticky top-0 z-50 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-4">
              <div 
                className="text-2xl font-bold text-orange-500 cursor-pointer"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              
              <button
                onClick={() => setShowMobileSidebar(true)}
                className="md:hidden flex items-center space-x-2 px-3 py-2 bg-orange-500 text-white rounded-lg"
              >
                <Menu size={20} />
              </button>
            </div>
            
            <div className="flex-1 max-w-xl mx-6 hidden sm:block">
              <form onSubmit={handleSearch} className="relative">
                <input 
                  type="text" 
                  placeholder="Recherchez un produit..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-white placeholder-gray-400"
                />
                <button type="submit" className="absolute right-3 top-2.5 text-gray-300 hover:text-orange-500">
                  <Search size={20} />
                </button>
              </form>
            </div>

            <div className="flex items-center space-x-2">
              {user ? (
                <>
                  {/* Bouton Ajouter un produit - Uniquement pour les vendeurs */}
                  {user.user_type === 'vendeur' && (
  <button 
    onClick={() => navigate('/vendor/add-product')}
    className="p-2 text-white hover:text-green-500 hover:bg-green-500/20 rounded-lg transition"
    title="Ajouter un produit"
  >
    <Plus size={22} />
  </button>
)}

                  <button 
                    onClick={() => navigate('/cart')} 
                    className="relative p-2 text-white hover:text-orange-500 hover:bg-white/10 rounded-lg transition"
                    title="Panier"
                  >
                    <ShoppingCart size={22} />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {cartCount}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => navigate('/orders')}
                    className="p-2 text-white hover:text-orange-500 hover:bg-white/10 rounded-lg transition"
                    title="Mes commandes"
                  >
                    <ShoppingBag size={22} />
                  </button>
                  
                  <button 
                    onClick={() => navigate('/profile/edit')}
                    className="p-2 text-white hover:text-orange-500 hover:bg-white/10 rounded-lg transition"
                    title="Paramètres"
                  >
                    <Settings size={22} />
                  </button>
                  
                  <button 
                    onClick={() => navigate(user.user_type === 'vendeur' ? '/vendor/dashboard' : '/buyer/dashboard')}
                    className="hidden sm:flex items-center space-x-2 text-white hover:text-orange-500 transition group" // Ajout de 'group' pour faciliter l'animation future
                  >
                    <div className="relative">
                      {getUserAvatar() ? (
                        <img 
                          src={getUserAvatar()} 
                          alt={user.username}
                          className="w-9 h-9 rounded-full object-cover border-2 border-orange-200"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/36x36?text=' + user.username[0].toUpperCase();
                          }}
                        />
                      ) : (
                        <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.username[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    
                    {/* Affichage conditionnel du message de bienvenue */}
                    <span className="text-sm font-bold relative overflow-hidden uppercase">
  <span 
    className={`transition-all duration-500 ease-in-out whitespace-nowrap ${
      showWelcomeMessage 
        ? 'translate-x-0 opacity-100 max-w-xs mr-1' 
        : 'translate-x-1 opacity-0 max-w-0 pointer-events-none absolute'
    }`}
  >
    BIENVENU(E)
  </span>
  <span className="text-orange-400">
    {user.username.toUpperCase()}
  </span>
</span>
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition ml-2"
                    title="Déconnexion"
                  >
                    <LogOut size={22} />
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => navigate('/login')} 
                    className="hidden sm:block text-white hover:text-orange-500 transition font-medium"
                  >
                    Connexion
                  </button>
                  <button 
                    onClick={() => navigate('/register')} 
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition font-medium text-sm"
                  >
                    Inscription
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Mobile */}
      {showMobileSidebar && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-70 z-40"
            onClick={() => setShowMobileSidebar(false)}
          ></div>
          <div className="fixed top-0 left-0 h-full w-80 bg-white/10 backdrop-blur-2xl z-50 shadow-2xl overflow-y-auto border-r border-white/20">
            <div className="p-4 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Catégories</h2>
              <button onClick={() => setShowMobileSidebar(false)}>
                <X size={24} className="text-white" />
              </button>
            </div>
            <div className="p-2">
              {categories.map((cat) => {
                const IconComponent = categoryIcons[cat.name] || Package;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      filterByCategory(cat.id);
                      setShowMobileSidebar(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-white/10 transition text-left rounded-lg"
                  >
                    <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-orange-400" size={20} />
                    </div>
                    <span className="text-white font-medium">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8 relative z-10">
        {/* Barre de recherche mobile */}
        <div className="sm:hidden mb-6">
          <form onSubmit={handleSearch} className="relative">
            <input 
              type="text" 
              placeholder="Recherchez un produit..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-white placeholder-gray-400"
            />
            <button type="submit" className="absolute right-3 top-2.5 text-gray-300 hover:text-orange-500">
              <Search size={20} />
            </button>
          </form>
        </div>

        {/* Slider de catégories horizontales avec flèches */}
        <div className="mb-8 overflow-hidden">
          <div className="flex items-center space-x-3 mb-4">
            <Package className="text-orange-500" size={24} />
            <h2 className="text-xl font-bold text-white">Explorer par catégorie</h2>
          </div>
          <div className="relative group">
            {/* Flèche gauche */}
            {showLeftArrow && (
              <button
                onClick={() => scrollCategories('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-md hover:bg-white/20 p-2 rounded-full shadow-lg transition-all border border-white/20"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
            )}

            <div 
              ref={categoryScrollRef}
              className="flex overflow-x-auto pb-4 gap-3 scrollbar-hide scroll-smooth"
            >
              <button
                onClick={() => filterByCategory(null)}
                className={`flex-shrink-0 px-6 py-3 rounded-xl font-medium transition-all ${
                  !selectedCategory 
                    ? 'bg-orange-500 text-white shadow-lg' 
                    : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                Tout
              </button>
              {categories.map((cat) => {
                const IconComponent = categoryIcons[cat.name] || Package;
                return (
                  <button
                    key={cat.id}
                    onClick={() => filterByCategory(cat.id)}
                    className={`flex-shrink-0 flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                      selectedCategory === cat.id 
                        ? 'bg-orange-500 text-white shadow-lg' 
                        : 'bg-white/10 backdrop-blur-md text-white hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    <IconComponent size={18} />
                    <span>{cat.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Flèche droite */}
            {showRightArrow && (
              <button
                onClick={() => scrollCategories('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/10 backdrop-blur-md hover:bg-white/20 p-2 rounded-full shadow-lg transition-all border border-white/20"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            )}
          </div>
        </div>

        {/* Message de résultat de recherche */}
        {searchQuery && (
          <div className="mb-6 flex items-center justify-between bg-orange-500/20 backdrop-blur-md border border-orange-400/30 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Search className="text-orange-400" size={20} />
              <span className="text-white">
                <span className="font-semibold">{products.length}</span> résultat{products.length > 1 ? 's' : ''} pour 
                <span className="font-semibold text-orange-400"> "{searchQuery}"</span>
              </span>
            </div>
            <button
              onClick={() => {
                setSearchQuery('');
                setProducts(allProducts);
              }}
              className="text-orange-400 hover:text-orange-300 font-medium text-sm"
            >
              Effacer
            </button>
          </div>
        )}

        {/* Produits */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <TrendingUp className="text-red-500" size={28} />
              <h2 className="text-2xl font-bold text-white">
                {searchQuery ? 'Résultats de recherche' : selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'Produits disponibles'}
              </h2>
            </div>
            <span className="text-sm text-gray-300">{products.length} produit{products.length > 1 ? 's' : ''}</span>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-16 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <Package className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-300 text-lg">
                {searchQuery 
                  ? `Aucun produit trouvé pour "${searchQuery}"`
                  : 'Aucun produit disponible pour le moment'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setProducts(allProducts);
                  }}
                  className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                >
                  Voir tous les produits
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product) => {
                const productImages = getProductImages(product);
                const currentIndex = currentImageIndex[product.id] || 0;
                const isFavorite = favorites.has(product.id);
                
                return (
                  <div 
                    key={product.id}
                    className="bg-white/10 backdrop-blur-md rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer group border border-white/20"
                    onClick={() => navigate(`/product/${product.id}`)}
                    onMouseEnter={() => setHoveredProduct(product.id)}
                    onMouseLeave={() => setHoveredProduct(null)}
                  >
                    {/* Image du produit avec slider */}
                    <div className="relative overflow-hidden bg-gray-800" style={{ paddingBottom: '75%' }}>
                      <img 
                        src={getImageUrl(productImages[currentIndex])}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                        }}
                      />
                      
                      {/* Badge durée */}
                      <div className="absolute top-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center space-x-1">
                        <Clock size={12} />
                        <span>{getTimeAgo(product.created_at)}</span>
                      </div>

                      
                        

                      {/* Navigation des images */}
                      {productImages.length > 1 && hoveredProduct === product.id && (
                        <>
                          <button
                            onClick={(e) => prevImage(product.id, productImages.length, e)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 p-1.5 rounded-full shadow-lg transition border border-white/30"
                          >
                            <ChevronLeft size={20} className="text-white" />
                          </button>
                          <button
                            onClick={(e) => nextImage(product.id, productImages.length, e)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-md hover:bg-white/30 p-1.5 rounded-full shadow-lg transition border border-white/30"
                          >
                            <ChevronRight size={20} className="text-white" />
                          </button>

                          {/* Indicateurs de position */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-1.5">
                            {productImages.map((_, idx) => (
                              <div
                                key={idx}
                                className={`w-1.5 h-1.5 rounded-full transition-all ${
                                  currentIndex === idx ? 'bg-orange-500 w-4' : 'bg-white/70'
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Contenu de la carte */}
                    <div className="p-4">
                      {/* Prix */}
                      <div className="mb-3">
                        <span className="text-2xl font-bold text-orange-400">
                          {product.price?.toLocaleString()} 
                        </span>
                        <span className="text-sm text-gray-300 ml-1">CFA</span>
                      </div>

                      {/* Titre du produit */}
                      <h3 className="font-semibold text-white mb-3 line-clamp-2 text-base leading-tight min-h-[2.5rem]">
                        {product.name}
                      </h3>

                      

                      {/* Informations vendeur */}
                      <div className="flex items-center justify-between pt-3 border-t border-white/20">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          {/* Avatar du vendeur */}
                          {product.vendor_profile_photo ? (
                            <img 
                              src={getImageUrl(product.vendor_profile_photo)}
                              alt={product.vendor_name}
                              className="w-8 h-8 rounded-full object-cover border-2 border-orange-200 flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 ${
                              product.vendor_profile_photo ? 'hidden' : 'flex'
                            }`}
                          >
                            {(product.vendor_name || 'V')[0].toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-xs font-medium text-white truncate">
                              {product.vendor_name || 'Vendeur'}
                            </span>
                            <div className="flex items-center space-x-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={10} 
                                  className={i < 4 ? 'text-yellow-400 fill-current' : 'text-gray-400'}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Badge PRO */}
                        <div className="bg-blue-500/20 backdrop-blur-sm text-blue-400 px-2 py-1 rounded text-xs font-bold flex-shrink-0 border border-blue-400/30">
                          PRO
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white/5 backdrop-blur-md text-white mt-16 border-t border-white/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Colonne 1 - Branding */}
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-4">MonMarket</div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                Le chemin le plus court entre vous et vos produits en Côte d'Ivoire.
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 bg-orange-500/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500 transition border border-orange-400/30">
                  <Facebook size={20} />
                </a>
                <a href="#" className="w-10 h-10 bg-orange-500/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500 transition border border-orange-400/30">
                  <MessageCircle size={20} />
                </a>
                <a href="#" className="w-10 h-10 bg-orange-500/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500 transition border border-orange-400/30">
                  <Camera size={20} />
                </a>
                <a href="#" className="w-10 h-10 bg-orange-500/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-orange-500 transition border border-orange-400/30">
                  <Monitor size={20} />
                </a>
              </div>
            </div>

            {/* Colonne 2 - Navigation */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-white">Navigation</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/" className="text-gray-300 hover:text-orange-400 transition text-sm flex items-center space-x-2">
                    <Home size={16} />
                    <span>Accueil</span>
                  </a>
                </li>
                <li>
  <a
    href="/help"
    className="text-gray-300 hover:text-orange-400 transition text-sm flex items-center space-x-2"
  >
    <HelpCircle size={16} />
    <span>Aide & Assistance</span>
  </a>
</li>
                <li>
                  <a href="/about" className="text-gray-300 hover:text-orange-400 transition text-sm flex items-center space-x-2">
                    <Info size={16} /> 
                    <span>À propos</span>
                  </a>
                </li>
                <li>
  <a
    href="/cgu"
    className="text-gray-300 hover:text-orange-400 transition text-sm flex items-center space-x-2"
  >
    <ShieldCheck size={16} />
    <span>Informations Légales</span>
  </a>
</li>
              </ul>
            </div>

            {/* Colonne 3 - Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-white">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2 text-gray-300 text-sm">
                  <Phone size={16} className="mt-0.5 flex-shrink-0" />
                  <span>+225 01 42 50 77 50</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300 text-sm">
                  <Mail size={16} className="mt-0.5 flex-shrink-0" />
                  <span>contact@monmarket.ci</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300 text-sm">
                  <MapPinned size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Abidjan, Côte d'Ivoire</span>
                </li>
              </ul>
            </div>

            {/* Colonne 4 - Newsletter */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-white">Newsletter</h3>
              <p className="text-gray-300 text-sm mb-4">
                Recevez nos meilleures offres
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col space-y-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                  required
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-semibold text-sm"
                >
                  S'abonner
                </button>
              </form>
            </div>
          </div>

          {/* Ligne de séparation */}
          <div className="border-t border-white/10 pt-6">
            <p className="text-center text-gray-400 text-sm">
              © 2025 MonMarket CI • Tous droits réservés • La marketplace sécurisée en Côte d'Ivoire
            </p>
          </div>
        </div>
      </footer>

      {/* Style pour masquer la scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}